/**
 * PinDetailPanel
 *
 * Floating, draggable panel that appears when a campaign map pin is selected.
 * Shows pin info and provides GM controls for state management.
 */

import { useRef, useState, useCallback } from 'react';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useIsGM } from '../hooks/useIsGM';
import { getPinStateColor } from '@machina/core';
import type { PinState } from '@machina/core';
import { Icon } from './Icon';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';
const SILVER = '#c0c0c0';
const SILVER_DIM = 'rgba(192, 192, 192, 0.4)';

const PIN_STATE_LABELS: Record<PinState, string> = {
  hidden: 'Hidden',
  discovered: 'Discovered',
  active: 'Active',
  completed: 'Completed',
};

export function PinDetailPanel() {
  const selectedPinId = useCampaignMapStore((s) => s.selectedPinId);
  const getPinById = useCampaignMapStore((s) => s.getPinById);
  const selectPin = useCampaignMapStore((s) => s.selectPin);
  const setPinState = useCampaignMapStore((s) => s.setPinState);
  const travelToPin = useCampaignMapStore((s) => s.travelToPin);
  const partyLocation = useCampaignMapStore((s) => s.partyLocation);
  const isGM = useIsGM();

  // Drag state
  const [pos, setPos] = useState({ x: 16, y: 60 }); // below header
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const onDragStart = useCallback((e: React.MouseEvent) => {
    // Only drag from left mouse button
    if (e.button !== 0) return;
    dragging.current = true;
    dragOffset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: ev.clientX - dragOffset.current.x,
        y: Math.max(56, ev.clientY - dragOffset.current.y), // clamp below header
      });
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  if (!selectedPinId) return null;

  const pin = getPinById(selectedPinId);
  if (!pin) return null;

  const stateColor = getPinStateColor(pin.state);
  const isPartyHere = partyLocation?.currentPinId === pin.id;

  const handleClose = () => selectPin(null);

  const handleTravel = async () => {
    await travelToPin(pin.id);
  };

  const handleStateChange = async (state: PinState) => {
    await setPinState(pin.id, state);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: pos.y,
        left: pos.x,
        width: 280,
        backgroundColor: 'rgba(10, 8, 12, 0.95)',
        border: `1px solid ${GOLD_DIM}`,
        borderRadius: 6,
        boxShadow: `0 4px 20px rgba(0, 0, 0, 0.5), 0 0 15px ${GOLD}15`,
        zIndex: 200,
        overflow: 'hidden',
      }}
    >
      {/* Header — draggable */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: `1px solid ${SILVER_DIM}`,
          backgroundColor: 'rgba(20, 15, 25, 0.8)',
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: stateColor,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#f0e8f8',
              fontFamily: 'Georgia, serif',
            }}
          >
            {pin.name}
          </span>
        </div>
        <button
          onClick={handleClose}
          style={{
            padding: 4,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: SILVER_DIM,
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Pin type & state */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              padding: '2px 8px',
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 3,
              fontSize: 10,
              color: SILVER,
              textTransform: 'capitalize',
            }}
          >
            {pin.iconType}
          </span>
          <span
            style={{
              padding: '2px 8px',
              backgroundColor: `${stateColor}20`,
              border: `1px solid ${stateColor}`,
              borderRadius: 3,
              fontSize: 10,
              color: stateColor,
            }}
          >
            {PIN_STATE_LABELS[pin.state]}
          </span>
          {isPartyHere && (
            <span
              style={{
                padding: '2px 8px',
                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                border: '1px solid #f59e0b',
                borderRadius: 3,
                fontSize: 10,
                color: '#f59e0b',
              }}
            >
              Party Here
            </span>
          )}
        </div>

        {/* Description */}
        {pin.description && (
          <p
            style={{
              fontSize: 12,
              color: SILVER,
              lineHeight: 1.5,
              margin: 0,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
            }}
          >
            {pin.description}
          </p>
        )}

        {/* Notes */}
        {pin.notes && (
          <div
            style={{
              padding: 8,
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
              borderLeft: `3px solid ${GOLD_DIM}`,
            }}
          >
            <div style={{ fontSize: 9, color: GOLD, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              Notes
            </div>
            <div style={{ fontSize: 11, color: '#f0e8f8', lineHeight: 1.4 }}>
              {pin.notes}
            </div>
          </div>
        )}

        {/* Tags */}
        {pin.tags && pin.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {pin.tags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '1px 6px',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: 2,
                  fontSize: 9,
                  color: SILVER_DIM,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Linked scene indicator */}
        {pin.linkedSceneId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: GOLD }}>
            <Icon name="map" size={12} color={GOLD} />
            <span>Scene linked</span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          {/* Travel button */}
          {!isPartyHere && (
            <button
              onClick={handleTravel}
              style={{
                padding: '8px 12px',
                background: 'linear-gradient(180deg, rgba(139, 69, 19, 0.6) 0%, rgba(80, 40, 10, 0.8) 100%)',
                border: `1px solid ${GOLD_DIM}`,
                borderRadius: 3,
                color: GOLD,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Georgia, serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Icon name="target" size={12} color={GOLD} />
              Travel Here
            </button>
          )}

          {/* GM-only: State controls */}
          {isGM && (
            <div>
              <div style={{ fontSize: 9, color: SILVER_DIM, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                Set State
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['hidden', 'discovered', 'active', 'completed'] as PinState[]).map((state) => {
                  const color = getPinStateColor(state);
                  const isActive = pin.state === state;
                  return (
                    <button
                      key={state}
                      onClick={() => handleStateChange(state)}
                      style={{
                        flex: 1,
                        padding: '4px 6px',
                        fontSize: 9,
                        fontWeight: isActive ? 600 : 400,
                        backgroundColor: isActive ? `${color}30` : 'rgba(0,0,0,0.3)',
                        border: `1px solid ${isActive ? color : SILVER_DIM}`,
                        borderRadius: 3,
                        color: isActive ? color : SILVER_DIM,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {state}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
